import {
  $addUpdateTag,
  $createNodeSelection,
  $createRangeSelection,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  CLEAR_HISTORY_COMMAND,
  HISTORY_MERGE_TAG,
  SKIP_DOM_SELECTION_TAG,
  type ElementNode,
  type LexicalEditor,
  type LexicalNode,
  type PointType,
  type SerializedEditorState,
} from "lexical";
import type { BlockId } from "../../core/types";
import { parseContent, type ErrorWatch } from "../../lexical/parse";
import { $forEachBlock, $rootBlockOf } from "../block-id/operations";
import { $getBlockId, $isIdentified } from "../block-id/state";
import { $selectedBlocks } from "../block-selection/extension";

/** What survives when a stored copy replaces the live content. See `RevisionPolicy`. */
export interface ReplacePolicy {
  selection: "keep" | "reset";
  history: "clear" | "keep";
}

/**
 * One end of a selection, in terms that outlive the nodes: a block's ID and a count of characters,
 * and where its root-level block stood, for when the block itself is gone.
 */
interface PointBookmark {
  blockId: BlockId;
  offset: number;
  rootIndex: number;
}

type SelectionBookmark =
  | { kind: "range"; anchor: PointBookmark | null; focus: PointBookmark | null }
  | { kind: "blocks"; blockIds: readonly BlockId[] }
  /** The start of the document: what `selection: 'reset'` asks for. */
  | { kind: "start" };

/** A line break, or anything else without text, counts as one character, as it does in plain text. */
const leafSize = (node: LexicalNode): number => ($isTextNode(node) ? node.getTextContentSize() : 1);

/** How many characters of `block` come before `point`. */
function $offsetInBlock(block: ElementNode, point: PointType): number {
  const target = point.getNode();
  let count = 0;
  let found = false;
  const walk = (parent: ElementNode): void => {
    const children = parent.getChildren();
    for (let index = 0; index < children.length && !found; index++) {
      if (point.type === "element" && parent.is(target) && index === point.offset) {
        found = true;
        return;
      }
      const child = children[index]!;
      if (point.type === "text" && child.is(target)) {
        count += point.offset;
        found = true;
      } else if ($isElementNode(child)) walk(child);
      else count += leafSize(child);
    }
    // An element point past its last child: everything inside came before it, nothing after did.
    if (parent.is(target)) found = true;
  };
  walk(block);
  return count;
}

function $bookmarkPoint(point: PointType): PointBookmark | null {
  let node: LexicalNode | null = point.getNode();
  while (node !== null && !($isElementNode(node) && $isIdentified(node))) node = node.getParent();
  if (node === null || !$isElementNode(node)) return null;
  const blockId = $getBlockId(node);
  if (blockId === null) return null;
  const rootIndex = $rootBlockOf(node)?.getIndexWithinParent() ?? 0;
  return { blockId, offset: $offsetInBlock(node, point), rootIndex };
}

function $captureSelection(): SelectionBookmark | null {
  const blocks = $selectedBlocks();
  if (blocks !== null) {
    const blockIds = blocks.map($getBlockId).filter((id): id is BlockId => id !== null);
    return { kind: "blocks", blockIds };
  }
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;
  return {
    kind: "range",
    anchor: $bookmarkPoint(selection.anchor),
    focus: $bookmarkPoint(selection.focus),
  };
}

interface ResolvedPoint {
  key: string;
  offset: number;
  type: "text" | "element";
}

/** The place `offset` characters into `block`, or its end when the block is now shorter. */
function $resolveInBlock(block: ElementNode, offset: number): ResolvedPoint {
  let remaining = offset;
  let resolved: ResolvedPoint | null = null;
  const walk = (parent: ElementNode): void => {
    const children = parent.getChildren();
    for (let index = 0; index < children.length && resolved === null; index++) {
      const child = children[index]!;
      if ($isElementNode(child)) {
        walk(child);
      } else if ($isTextNode(child)) {
        const size = child.getTextContentSize();
        if (remaining <= size) {
          // A token is one unit: a caret never rests inside it.
          const inside = child.isToken() && remaining > 0 && remaining < size;
          const at = inside ? (remaining * 2 < size ? 0 : size) : remaining;
          resolved = { key: child.getKey(), offset: at, type: "text" };
        } else remaining -= size;
      } else if (remaining <= 0) {
        resolved = { key: parent.getKey(), offset: index, type: "element" };
      } else remaining -= 1;
    }
  };
  walk(block);
  return resolved ?? { key: block.getKey(), offset: block.getChildrenSize(), type: "element" };
}

/** The start of the block now standing where a vanished one stood, or the end of the last one. */
function $resolveByPosition(rootIndex: number): ResolvedPoint | null {
  const blocks = $getRoot().getChildren();
  const at = blocks.slice(rootIndex).find($isElementNode);
  if (at !== undefined) return $resolveInBlock(at, 0);
  const last = blocks.findLast($isElementNode);
  return last === undefined ? null : $resolveInBlock(last, Number.POSITIVE_INFINITY);
}

/** Runs against the new content, where the nodes are new and only the block IDs are familiar. */
function $restoreSelection(bookmark: SelectionBookmark): void {
  if (bookmark.kind === "start") return void $getRoot().selectStart();
  const wanted = new Set<BlockId>(
    bookmark.kind === "blocks"
      ? bookmark.blockIds
      : [bookmark.anchor?.blockId, bookmark.focus?.blockId].filter(
          (id): id is BlockId => id !== undefined,
        ),
  );
  const found = new Map<BlockId, ElementNode>();
  $forEachBlock((node, id) => {
    if (id !== null && wanted.has(id) && $isElementNode(node)) found.set(id, node);
  });

  if (bookmark.kind === "blocks") {
    const root = $getRoot();
    const selection = $createNodeSelection();
    for (const id of bookmark.blockIds) {
      const node = found.get(id);
      // Only root-level blocks are ever selected as blocks; one that moved into a list is not.
      if (node !== undefined && root.is(node.getParent())) selection.add(node.getKey());
    }
    return void $setSelection(selection.getNodes().length > 0 ? selection : null);
  }

  const resolve = (point: PointBookmark | null): ResolvedPoint | null => {
    if (point === null) return null;
    const block = found.get(point.blockId);
    return block === undefined
      ? $resolveByPosition(point.rootIndex)
      : $resolveInBlock(block, point.offset);
  };
  const anchor = resolve(bookmark.anchor) ?? resolve(bookmark.focus);
  const focus = resolve(bookmark.focus) ?? anchor;
  // "No caret" is not something a focused editor can be left with: Chromium then puts one of its
  // own at the top and types there, while Firefox and WebKit type nothing. Say where it is.
  if (anchor === null || focus === null) return void $getRoot().selectStart();
  const selection = $createRangeSelection();
  selection.anchor.set(anchor.key, anchor.offset, anchor.type);
  selection.focus.set(focus.key, focus.offset, focus.type);
  $setSelection(selection);
}

/**
 * Replaces what the editor shows with stored content, in place: the editor, its DOM element, and
 * its focus stay. It is a load, not an edit: it merges into history instead of adding a step
 * (which also keeps transforms that rewrite text, like autolinking, silent), and the caller must
 * not count the update it causes as a change. Answers `false` when the engine refused the content
 * (which it reports rather than throws: see `parseContent`); the editor then still shows what it
 * showed, and the caller rebuilds it. That is how an editor without tables meets its first table.
 */
export function replaceContent(
  editor: LexicalEditor,
  content: SerializedEditorState,
  policy: ReplacePolicy,
  errors: ErrorWatch,
): boolean {
  const bookmark: SelectionBookmark | null =
    policy.selection === "keep" ? editor.read($captureSelection) : { kind: "start" };
  const parsed = parseContent(editor, content, errors, () => $setSelection(null));
  if (!parsed.ok) return false;
  try {
    const next = parsed.state;
    // Nothing older than the new content can be reached afterwards. (Clearing before or after the
    // state is set was tested both ways and behaves the same.)
    if (policy.history === "clear") editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
    editor.setEditorState(next, { tag: HISTORY_MERGE_TAG });
  } catch {
    return false;
  }
  if (bookmark === null) return true;
  // The selection goes back in an update of its own, because `setEditorState` takes one tag and
  // this needs another: an editor that does not have focus must not be given a DOM selection,
  // which in Firefox takes focus along. Lexical still remembers where the caret belongs. Nothing
  // is dirtied here, so it is not an edit either.
  const root = editor.getRootElement();
  const focused = root !== null && root.contains(root.ownerDocument.activeElement);
  editor.update(
    () => {
      $addUpdateTag(HISTORY_MERGE_TAG);
      if (!focused) $addUpdateTag(SKIP_DOM_SELECTION_TAG);
      $restoreSelection(bookmark);
    },
    { discrete: true },
  );
  return true;
}

export interface Settler {
  /** Resolves once no IME composition is open; at once if none is. */
  settled(): Promise<void>;
  /**
   * Lets everyone waiting go and stops listening. Lexical ends a composition itself when its root
   * element goes away, so in practice waiters are already free by then; this makes sure of it.
   */
  dispose(): void;
}

/**
 * Replacing the editor state ends a composition as far as Lexical knows (`setEditorState` clears
 * its composition key) while the IME still has one open, which destroys what is being typed. So
 * whoever wants to replace content waits here first.
 */
export function createSettler(editor: LexicalEditor): Settler {
  const waiting = new Set<() => void>();
  let stop: (() => void) | null = null;

  const release = (): void => {
    stop?.();
    stop = null;
    for (const resolve of waiting) resolve();
    waiting.clear();
  };
  const check = (): void => {
    if (!editor.isComposing()) release();
  };
  const watch = (): void => {
    if (stop !== null) return;
    const offUpdate = editor.registerUpdateListener(check);
    // A composition that ends without changing anything may not cause an update.
    const onEnd = (): void => void setTimeout(check, 0);
    const offRoot = editor.registerRootListener((root, previous) => {
      previous?.removeEventListener("compositionend", onEnd);
      root?.addEventListener("compositionend", onEnd);
    });
    stop = () => {
      offUpdate();
      offRoot();
      editor.getRootElement()?.removeEventListener("compositionend", onEnd);
    };
  };

  return {
    settled() {
      // Commit what is pending first, so an edit made a moment ago is counted before anyone looks.
      editor.read(() => null);
      if (!editor.isComposing()) return Promise.resolve();
      return new Promise<void>((resolve) => {
        waiting.add(resolve);
        watch();
      });
    },
    dispose: release,
  };
}
